/**
 * Shared helpers for Conduct / Evaluate Interview flows (CandidateRow + page-level modals).
 */

export function questionnaireHasAnyQuestions(parsed) {
    if (!parsed) return false;
    return Boolean(
        (parsed.introduction && parsed.introduction.length > 0) ||
        (parsed.behavioral && parsed.behavioral.length > 0) ||
        (parsed.situational && parsed.situational.length > 0) ||
        (parsed.technical &&
            ((parsed.technical.theoretical && parsed.technical.theoretical.length > 0) ||
                (parsed.technical.practical && parsed.technical.practical.length > 0)))
    );
}

/** Parsed questionnaire from resume.questionnaire or evaluation_payload.questionnaire (for re-evaluate). */
export function parseQuestionnaireFromCandidate(candidate) {
    if (!candidate) return null;
    if (candidate.questionnaire) {
        try {
            const parsed =
                typeof candidate.questionnaire === 'string'
                    ? JSON.parse(candidate.questionnaire)
                    : candidate.questionnaire;
            if (questionnaireHasAnyQuestions(parsed)) return parsed;
        } catch (e) {
            console.error('Error parsing candidate.questionnaire:', e);
        }
    }
    const ep = candidate.evaluation_payload;
    if (ep) {
        try {
            const p = typeof ep === 'string' ? JSON.parse(ep) : ep;
            const q = p?.questionnaire;
            if (q && questionnaireHasAnyQuestions(q)) return q;
        } catch (e) {
            console.error('Error parsing evaluation_payload for questionnaire:', e);
        }
    }
    return null;
}

/**
 * Questionnaire prop for InterviewEvaluationModal: only return data that actually contains
 * at least one question in the expected shape. Otherwise return null so the UI opens choice
 * mode or triggers generation — empty `{}` or all-empty arrays in DB must not produce a
 * blank "Candidate Responses" screen.
 */
export function getQuestionnaireForInterviewModal(candidate) {
    const parsed = parseQuestionnaireFromCandidate(candidate);
    if (parsed) return parsed;
    if (!candidate?.questionnaire) return null;
    try {
        const q =
            typeof candidate.questionnaire === 'string'
                ? JSON.parse(candidate.questionnaire)
                : candidate.questionnaire;
        if (typeof q !== 'object' || q === null) return null;
        return questionnaireHasAnyQuestions(q) ? q : null;
    } catch {
        return null;
    }
}

/** Saved interview AI evaluation from API (snake_case or camelCase). */
export function normalizeInterviewEvaluationResults(candidate) {
    if (!candidate) return null;
    let r = candidate.evaluation_results ?? candidate.evaluationResults;
    if (r == null || r === '') return null;
    if (typeof r === 'string') {
        try {
            r = JSON.parse(r);
        } catch {
            return r.trim() ? r : null;
        }
    }
    if (typeof r === 'object' && r !== null && !Array.isArray(r) && Object.keys(r).length === 0) {
        return null;
    }
    return r;
}

/** Fit / calibrated score for interview modal (matches CandidateRow getScore().value). */
export function getCandidateFitScore(candidate) {
    if (!candidate) return 0;
    if (candidate.rag_analysis?.evaluation_result?.calibrated_score !== undefined) {
        return candidate.rag_analysis.evaluation_result.calibrated_score;
    }
    if (candidate.rag_analysis?.evaluation_result?.fit_score !== undefined) {
        return candidate.rag_analysis.evaluation_result.fit_score;
    }
    if (candidate.analysis?.fit_score !== undefined) {
        return candidate.analysis.fit_score;
    }
    if (candidate.analysis?.score !== undefined) {
        return candidate.analysis.score;
    }
    return 0;
}
